import type { BookFilters, FilterKey } from '@/types'
import { useTranslation } from 'react-i18next'
import FilterDropdown from './FilterDropdown'
import { FORMAT_OPTIONS, STATUS_OPTIONS, PROGRESS_OPTIONS } from '../constants'

type FilterVisibilityConfig = Record<string, boolean>

interface BookListFiltersProps {
    // Filter state
    filterValues: BookFilters
    updateFilter: (key: FilterKey, value: string[]) => void
    clearFilter: (key: FilterKey) => void
    clearAllFilters: () => void
    hasActiveFilters: boolean

    // Filter visibility
    visibleFilters: FilterVisibilityConfig
    setVisibleFilters: (config: FilterVisibilityConfig | ((prev: FilterVisibilityConfig) => FilterVisibilityConfig)) => void
    showFilterSettings: boolean
    setShowFilterSettings: (show: boolean) => void

    // Dynamic options getters
    getUniqueLanguages: () => string[]
    getUniqueCategories: () => string[]
    getUniqueAuthors: () => string[]
    getUniqueSeries: () => string[]
    getUniquePublishers: () => string[]
    getUniqueFormats: () => readonly string[]
    getUniqueStatuses: () => readonly string[]
    getUniqueProgress: () => readonly string[]
    getUniqueLocations: () => string[]
    getUniqueBiblionautas: () => string[]

    // Context
    isBiblioverso: boolean
    isSharedView?: boolean
    sharedSettings?: string[] | null
}

export default function BookListFilters({
    filterValues,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasActiveFilters,
    visibleFilters,
    setVisibleFilters,
    showFilterSettings,
    setShowFilterSettings,
    getUniqueLanguages,
    getUniqueCategories,
    getUniqueAuthors,
    getUniqueSeries,
    getUniquePublishers,
    getUniqueFormats,
    getUniqueStatuses,
    getUniqueProgress,
    getUniqueLocations,
    getUniqueBiblionautas,
    isBiblioverso,
    isSharedView = false,
    sharedSettings = null,
}: Readonly<BookListFiltersProps>) {
    const { t } = useTranslation()

    const isFieldAllowed = (fieldName: string): boolean => {
        if (isBiblioverso) return true
        if (!isSharedView) return true

        if (!sharedSettings || !Array.isArray(sharedSettings)) {
            return ['author', 'category'].includes(fieldName)
        }

        return sharedSettings.includes(fieldName)
    }

    return (
        <div className="filter-bar animate-fade-in-up stagger-2">
            {visibleFilters.language && isFieldAllowed('language') && (
                <FilterDropdown
                    label={t('bookList.filters.labels.language')}
                    icon="🌐"
                    options={getUniqueLanguages()}
                    selected={filterValues.language}
                    onSelect={(v) => updateFilter('language', v)}
                    onClear={() => clearFilter('language')}
                />
            )}
            {visibleFilters.biblionauta && isBiblioverso && (
                <FilterDropdown
                    label={t('bookList.filters.labels.biblionauta')}
                    icon="👩‍🚀"
                    options={getUniqueBiblionautas()}
                    selected={filterValues.biblionauta}
                    onSelect={(v) => updateFilter('biblionauta', v)}
                    onClear={() => clearFilter('biblionauta')}
                />
            )}
            {visibleFilters.category && isFieldAllowed('category') && (
                <FilterDropdown
                    label={t('bookList.filters.labels.category')}
                    icon="🏷️"
                    options={getUniqueCategories()}
                    selected={filterValues.category}
                    onSelect={(v) => updateFilter('category', v)}
                    onClear={() => clearFilter('category')}
                />
            )}
            {visibleFilters.author && isFieldAllowed('author') && (
                <FilterDropdown
                    label={t('bookList.filters.labels.author')}
                    icon="✍️"
                    options={getUniqueAuthors()}
                    selected={filterValues.author}
                    onSelect={(v) => updateFilter('author', v)}
                    onClear={() => clearFilter('author')}
                />
            )}
            {visibleFilters.series && isFieldAllowed('series') && (
                <FilterDropdown
                    label={t('bookList.filters.labels.series')}
                    icon="📚"
                    options={getUniqueSeries()}
                    selected={filterValues.series}
                    onSelect={(v) => updateFilter('series', v)}
                    onClear={() => clearFilter('series')}
                />
            )}
            {visibleFilters.publisher && isFieldAllowed('publisher') && (
                <FilterDropdown
                    label={t('bookList.filters.labels.publisher')}
                    icon="🏢"
                    options={getUniquePublishers()}
                    selected={filterValues.publisher}
                    onSelect={(v) => updateFilter('publisher', v)}
                    onClear={() => clearFilter('publisher')}
                />
            )}
            {visibleFilters.format && isFieldAllowed('format') && (
                <FilterDropdown
                    label={t('bookList.filters.labels.format')}
                    icon="📦"
                    options={getUniqueFormats().length > 0 ? getUniqueFormats() : FORMAT_OPTIONS}
                    selected={filterValues.format}
                    onSelect={(v) => updateFilter('format', v)}
                    onClear={() => clearFilter('format')}
                />
            )}
            {visibleFilters.status && isFieldAllowed('status') && (
                <FilterDropdown
                    label={t('bookList.filters.labels.status')}
                    icon="🏷️"
                    options={getUniqueStatuses().length > 0 ? getUniqueStatuses() : STATUS_OPTIONS}
                    selected={filterValues.status}
                    onSelect={(v) => updateFilter('status', v)}
                    onClear={() => clearFilter('status')}
                />
            )}
            {visibleFilters.progress && isFieldAllowed('progress') && (
                <FilterDropdown
                    label={t('bookList.filters.labels.progress')}
                    icon="📊"
                    options={getUniqueProgress().length > 0 ? getUniqueProgress() : PROGRESS_OPTIONS}
                    selected={filterValues.progress}
                    onSelect={(v) => updateFilter('progress', v)}
                    onClear={() => clearFilter('progress')}
                />
            )}
            {visibleFilters.location && isFieldAllowed('location') && (
                <FilterDropdown
                    label={t('bookList.filters.labels.location')}
                    icon="📍"
                    options={getUniqueLocations()}
                    selected={filterValues.location}
                    onSelect={(v) => updateFilter('location', v)}
                    onClear={() => clearFilter('location')}
                />
            )}

            {/* Filter Settings Toggle */}
            <div className="filter-settings-inline">
                <button
                    className={`filter-settings-btn ${showFilterSettings ? 'active' : ''}`}
                    onClick={() => setShowFilterSettings(!showFilterSettings)}
                    title={t('bookList.filters.configure')}
                >
                    ⚙️
                </button>

                {showFilterSettings && (
                    <div className="filter-settings-panel animate-fade-in-up">
                        <h4>{t('bookList.filters.show')}</h4>
                        <div className="filter-settings-grid">
                            {Object.keys(visibleFilters).map((key) => {
                                const allowed = isFieldAllowed(key)
                                return (
                                    <label
                                        key={key}
                                        className={`filter-setting-item ${allowed ? '' : 'disabled'}`}
                                        title={allowed ? '' : t('bookList.filters.notAllowed')}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={Object.hasOwn(visibleFilters, key) ? visibleFilters[key] : false}
                                            onChange={() => setVisibleFilters((prev) => ({ ...prev, [key]: !prev[key] }))}
                                            disabled={!allowed}
                                        />
                                        <span className="setting-checkbox"></span>
                                        <span className="setting-label">
                                            {t(`bookList.filters.labels.${key}`, { defaultValue: key })}
                                            {!allowed && ' 🔒'}
                                        </span>
                                    </label>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>

            {hasActiveFilters && (
                <button className="clear-all-filters" onClick={clearAllFilters}>
                    ✕ {t('bookList.filters.clearAll')}
                </button>
            )}
        </div>
    )
}
